import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faInfoCircle,
  faBus,
  faHistory,
  faCheck,
  faClock,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

export const Shuttle = () => {
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [selectedDepartureTime, setSelectedDepartureTime] = useState("");
  const [selectedDepartureDate, setSelectedDepartureDate] = useState("");
  const [selectedPickupPoint, setSelectedPickupPoint] = useState("");
  const [selectedReturnTime, setSelectedReturnTime] = useState("");
  const [selectedReturnDate, setSelectedReturnDate] = useState("Minggu, 26 Juli 2026");
  const [selectedReturnPoint, setSelectedReturnPoint] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [shuttleOrders, setShuttleOrders] = useState([]);
  const [quotas, setQuotas] = useState({
    departure: {},
    return: {},
  });
  const [tripType, setTripType] = useState("round-trip");
  const [selectedOrderId, setSelectedOrderId] = useState(null); // Used to track dynamically selected previously placed order
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const SHUTTLE_COLLECTION = "pendaftaranShuttle2026";
  const SHUTTLE_PRICE_PP = 300000;
  const SHUTTLE_PRICE_ONE_WAY = 200000;
  const SHUTTLE_QUOTA = 14;
  const EVENT_ID = "ULTRA24";

  const pickupPoints = [
    {
      name: "GBK/Parkir Timur/Sentosa",
      meetingPoint: "GBK / Parkir Timur / Sentosa",
    },
  ];

  const returnToPoints = [
    {
      name: "GBK/Parkir Timur",
      meetingPoint: "GBK / Parkir Timur",
    },
  ];

  const destinationCibodas = "Cibodas";

  const departureSchedule = [
    { date: "Jumat, 24 Juli 2026", times: ["10:00"] },
    { date: "Sabtu, 25 Juli 2026", times: ["09:00", "13:00"] },
  ];

  const returnSchedule = [
    { date: "Minggu, 26 Juli 2026", times: ["15:00", "16:00"] },
  ];

  // Flattened for compatibility with existing logic if needed
  const departureTimes = departureSchedule.flatMap((s) =>
    s.times.map((t) => `${s.date} ${t} WIB`)
  );

  const returnTimes = returnSchedule.flatMap((s) =>
    s.times.map((t) => `${s.date} ${t} WIB`)
  );

  const shuttleDetails = {
    kendaraan: "Hi Ace / Big Bravo / Big Alpha",
    harga: tripType === "round-trip" ? SHUTTLE_PRICE_PP : SHUTTLE_PRICE_ONE_WAY,
  };

  // Inisialisasi Midtrans Snap
  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    return () => clearInterval(checkSnap);
  }, []);

  // Fetch kuota keberangkatan dan kepulangan
  const fetchQuotas = useCallback(async () => {
    try {
      const departureQuotaData = {};
      const returnQuotaData = {};

      // Kuota keberangkatan
      for (const point of pickupPoints) {
        departureQuotaData[point.name] = {};
        for (const time of departureTimes) {
          const q = query(
            collection(db, SHUTTLE_COLLECTION),
            where("pickupPoint", "==", point.name),
            where("departureTime", "==", time),
            where("transaction_status", "==", "settlement")
          );
          const snapshot = await getDocs(q);
          departureQuotaData[point.name][time] = {
            booked: snapshot.size,
            available: SHUTTLE_QUOTA - snapshot.size,
            full: snapshot.size >= SHUTTLE_QUOTA,
          };
        }
      }

      // Kuota kepulangan
      for (const time of returnTimes) {
        const q = query(
          collection(db, SHUTTLE_COLLECTION),
          where("returnTime", "==", time),
          where("transaction_status", "==", "settlement")
        );
        const snapshot = await getDocs(q);
        returnQuotaData[time] = {
          booked: snapshot.size,
          available: SHUTTLE_QUOTA - snapshot.size,
          full: snapshot.size >= SHUTTLE_QUOTA,
        };
      }

      setQuotas({ departure: departureQuotaData, return: returnQuotaData });
    } catch (err) {
      console.error("[FETCH] Gagal fetch kuota:", err);
      showNotification("error", "Gagal memuat data kuota shuttle.");
    }
  }, [showNotification]);

  // Ambil data pengguna dan pemesanan shuttle
  const fetchUserDataAndOrders = useCallback(async () => {
    if (!user?.uid || fetchStatus.current.isFetching) return;

    fetchStatus.current.isFetching = true;
    try {
      setInitialLoading(true);
      setError("");

      let userDataResult = null;
      let fetchedOrders = [];

      // Fetch user data
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        userDataResult = {
          ...userDoc.data(),
          email: userDoc.data().email || user.email,
        };
      } else {
        userDataResult = { email: user.email };
      }

      const shuttleQuery = query(
        collection(db, SHUTTLE_COLLECTION),
        where("uid", "==", user.uid)
      );
      const shuttleSnapshot = await getDocs(shuttleQuery);
      shuttleSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.eventId === EVENT_ID) {
          if (["pending", "settlement"].includes(data.transaction_status)) {
            fetchedOrders.push({ id: doc.id, ...data });
          }
        }
      });
      fetchedOrders.sort((a,b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      if (isMounted.current) {
        setUserData(userDataResult);
        setShuttleOrders(fetchedOrders);
        // Auto-select the first pending order
        const firstPending = fetchedOrders.find(o => o.transaction_status === 'pending');
        if (firstPending) {
          setSelectedOrderId(firstPending.id);
        }
      }
    } catch (err) {
      console.error("[ERROR] fetchUserDataAndPendingOrder:", {
        error: err.message,
        code: err.code,
      });
      if (isMounted.current) {
        setError(
          err.code === "permission-denied"
            ? "Akses ditolak. Anda tidak memiliki izin."
            : err.code === "unavailable"
            ? "Koneksi gagal. Periksa jaringan Anda."
            : "Gagal memuat data pengguna."
        );
        showNotification("error", "Gagal memuat data pengguna.");
      }
    } finally {
      if (isMounted.current) {
        setInitialLoading(false);
        setLoading(false);
      }
      fetchStatus.current.isFetching = false;
    }
  }, [user, showNotification]);

  // Efek awal
  useEffect(() => {
    isMounted.current = true;
    if (user?.uid) {
      fetchUserDataAndOrders();
      fetchQuotas();
      if (pickupPoints.length === 1) setSelectedPickupPoint(pickupPoints[0].name);
      if (returnToPoints.length === 1) setSelectedReturnPoint(returnToPoints[0].name);
      // Auto-select first date if available
      if (departureSchedule.length > 0) setSelectedDepartureDate(departureSchedule[0].date);
    } else {
      setUserData(null);
      setShuttleOrders([]);
      setInitialLoading(false);
      setLoading(false);
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, fetchUserDataAndOrders, fetchQuotas]);

  // Handle simulasi pemesanan (untuk pengujian)
  const handleSimulateBooking = useCallback(async () => {
    if (initialLoading) {
      showNotification("warning", "Sedang memuat data, silakan tunggu.");
      return;
    }
    if (!user?.uid) {
      showNotification(
        "error",
        "Silakan login untuk melakukan simulasi pemesanan."
      );
      return;
    }
    const isDepartureFull = (date, time) => {
      const fullTime = `${date} ${time} WIB`;
      return quotas.departure[selectedPickupPoint]?.[fullTime]?.full;
    };

    const isReturnFull = (date, time) => {
      const fullTime = `${date} ${time} WIB`;
      return quotas.return[fullTime]?.full;
    };

    if (tripType !== "one-way-return" && (!selectedDepartureTime || !selectedPickupPoint)) {
      showNotification(
        "error",
        "Silakan pilih jadwal dan lokasi keberangkatan."
      );
      return;
    }
    if (tripType !== "one-way-departure" && (!selectedReturnTime || !selectedReturnPoint)) {
      showNotification(
        "error",
        "Silakan pilih jadwal dan lokasi kepulangan."
      );
      return;
    }
    if (
      !userData?.namaLengkap ||
      !userData?.telepon ||
      !userData?.alamat ||
      !userData?.gender ||
      !userData?.email
    ) {
      showNotification(
        "error",
        "Data pengguna tidak lengkap: Nama Lengkap, No HP, Alamat, Gender, dan Email harus diisi."
      );
      return;
    }

    setLoading(true);
    try {
      const orderId = `SIM-${Date.now()}`;

      const shuttleData = {
        uid: user.uid,
        tripType,
        pickupPoint: tripType === "one-way-return" ? "-" : selectedPickupPoint,
        returnPoint: tripType === "one-way-departure" ? "-" : selectedReturnPoint,
        departureTime: tripType === "one-way-return" ? "-" : selectedDepartureTime,
        returnTime: tripType === "one-way-departure" ? "-" : selectedReturnTime,
        harga: shuttleDetails.harga,
        eventId: EVENT_ID,
        status: "settlement",
        createdAt: new Date().toISOString(),
        email: userData.email,
        namaLengkap: userData.namaLengkap,
        telepon: userData.telepon,
        alamat: userData.alamat,
        gender: userData.gender,
      };

      // Simpan data ke Firebase
      await setDoc(doc(db, SHUTTLE_COLLECTION, orderId), shuttleData);

      showNotification(
        "success",
        "Simulasi pemesanan berhasil! Data tersimpan di Firebase."
      );
      await fetchUserDataAndOrders();
      await fetchQuotas();
      navigate(
        `/myregistration?order_id=${orderId}&transaction_status=settlement`
      );
    } catch (err) {
      console.error(
        "[ERROR] handleSimulateBooking: Failed to simulate booking",
        {
          error: err.message,
        }
      );
      showNotification(
        "error",
        "Gagal melakukan simulasi pemesanan: " + err.message
      );
      setError("Gagal melakukan simulasi pemesanan: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    showNotification,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    navigate,
    fetchUserDataAndOrders,
    fetchQuotas,
    initialLoading,
  ]);

  // Handle pembayaran untuk order yang sudah ada
  const handleBayar = useCallback((snapToken) => {
    if (snapToken && window.snap) {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Pembayaran shuttle berhasil!");
          fetchUserDataAndOrders();
        },
        onPending: async () => {
          showNotification("warning", "Pembayaran shuttle tertunda.");
          fetchUserDataAndOrders();
        },
        onError: () => {
          showNotification("error", "Pembayaran shuttle gagal.");
        },
        onClose: () => {
          console.log("[PAYMENT] Jendela pembayaran ditutup.");
        },
      });
    } else {
      showNotification("error", "Token pembayaran tidak valid.");
    }
  }, [showNotification, fetchUserDataAndOrders]);

  // Handle konfirmasi pemesanan
  const handleConfirmBooking = useCallback(async () => {
    if (tripType !== "one-way-return" && !selectedPickupPoint) return;
    if (tripType !== "one-way-departure" && !selectedReturnPoint) return;

    setLoading(true);
    try {
      const token = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/shuttle-register`,
        {
          uid: user.uid,
          tripType,
          pickupPoint: tripType === "one-way-return" ? "-" : selectedPickupPoint,
          returnPoint: tripType === "one-way-departure" ? "-" : selectedReturnPoint,
          departureTime: tripType === "one-way-return" ? "-" : selectedDepartureTime,
          returnTime: tripType === "one-way-departure" ? "-" : selectedReturnTime,
          harga: shuttleDetails.harga,
          eventId: EVENT_ID,
          namaLengkap: userData?.namaLengkap || "",
          telepon: userData?.telepon || "",
          alamat: userData?.alamat || "",
          gender: userData?.gender || "",
          email: userData?.email || user.email || "",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { snapToken, orderId } = response.data;
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Pemesanan shuttle berhasil!");
          await fetchUserDataAndOrders();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          showNotification("warning", "Pembayaran shuttle belum selesai.");
          await fetchUserDataAndOrders();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          showNotification("error", "Pembayaran shuttle gagal.");
        },
        onClose: () => {},
      });
    } catch (err) {
      console.error("[ERROR] handleConfirmBooking: Failed to book shuttle", {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMessage = err.response?.data?.errors
        ? `Gagal memesan shuttle: ${err.response.data.errors.join(", ")}`
        : `Gagal memesan shuttle: ${
            err.response?.data?.message || err.message
          }`;
      showNotification("error", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    selectedPickupPoint,
    selectedReturnPoint,
    selectedDepartureTime,
    selectedReturnTime,
    userData,
    showNotification,
    navigate,
    fetchUserDataAndOrders,
    fetchQuotas,
    tripType,
    shuttleDetails.harga,
    getIdToken
  ]);

  const handleButtonClick = useCallback(() => {
    if (!user?.uid) {
      navigate("/login");
      showNotification("error", "Silakan login untuk memesan bus.");
      return;
    }
    if (!isSnapReady) {
      showNotification("error", "Midtrans sedang disiapkan, mohon tunggu.");
      return;
    }
    if (
      quotas.departure[selectedPickupPoint]?.[selectedDepartureTime]?.full
    ) {
      showNotification(
        "error",
        "Kuota keberangkatan untuk titik dan jadwal ini sudah penuh."
      );
      return;
    }
    if (quotas.return[selectedReturnTime]?.full) {
      showNotification(
        "error",
        "Kuota kepulangan untuk jadwal ini sudah penuh."
      );
      return;
    }
    if (tripType !== "one-way-return" && (!selectedDepartureTime || !selectedPickupPoint)) {
      showNotification(
        "error",
        "Silakan pilih jadwal dan lokasi keberangkatan."
      );
      return;
    }
    if (tripType !== "one-way-departure" && (!selectedReturnTime || !selectedReturnPoint)) {
      showNotification(
        "error",
        "Silakan pilih jadwal dan lokasi kepulangan."
      );
      return;
    }
    if (
      !userData?.namaLengkap ||
      !userData?.telepon ||
      !userData?.alamat ||
      !userData?.gender ||
      !userData?.email
    ) {
      showNotification(
        "error",
        "Data pengguna tidak lengkap: Nama Lengkap, No HP, Alamat, Gender, dan Email harus diisi."
      );
      return;
    }

    handleConfirmBooking();
  }, [
    user,
    navigate,
    showNotification,
    isSnapReady,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    initialLoading,
    handleConfirmBooking,
    tripType
  ]);

  return (
    <section className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Notifikasi Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="bg-red-50 text-red-700 p-4 rounded-2xl border border-red-200 mb-6 flex items-center justify-center shadow-none"
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-5 w-5 mr-3"
              />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col lg:flex-row gap-4 items-start mt-6">
          {/* Left Column (Orders + Form) */}
          <div className="w-full lg:flex-[2] flex flex-col gap-4">
            
            {/* Shuttle Orders List */}
            {shuttleOrders.length > 0 && (
              <div className="flex flex-col gap-4">
                {shuttleOrders.map((order) => {
                  const isPaid = order.transaction_status === "settlement" || order.transaction_status === "capture";
                  const isPending = order.transaction_status === "pending";
                  if (!isPaid && !isPending) return null;

                  const tripLabel = order.tripType === "round-trip" ? "Pulang Pergi" : order.tripType === "one-way-departure" ? "Berangkat Saja" : "Pulang Saja";

                  return (
                    <motion.div 
                      key={order.id} 
                      onClick={() => setSelectedOrderId(order.id)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl border border-slate-200 p-4"
                    >
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Registrasi Anda</h4>
                      {/* Header: Event Name + Status Badge */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-lg md:text-xl font-bold text-slate-800 uppercase">
                              Shuttle ULTRA24 - {tripLabel}
                            </h3>
                          </div>
                          {/* Location */}
                          <div className="text-sm text-red-600 mt-1 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            Cibodas, West Java - Indonesia
                          </div>
                        </div>
                      </div>

                      {/* Info Grid (2 Columns for Long Data) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Keberangkatan</div>
                          <div className="text-base font-semibold text-slate-800">
                            {order.tripType !== "one-way-return" ? (order.departureTime !== "-" ? order.departureTime : "—") : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Kepulangan</div>
                          <div className="text-base font-semibold text-slate-800">
                            {order.tripType !== "one-way-departure" ? (order.returnTime !== "-" ? order.returnTime : "—") : "—"}
                          </div>
                        </div>
                      </div>

                      {/* Info Grid - Standardized Alignment (12-col) */}
                      <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100">
                        <div className="col-span-2 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Order ID</div>
                          <div className="text-sm font-medium text-slate-700 font-mono md:whitespace-nowrap break-words">
                            {order.orderId || order.id}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Transaction Time</div>
                          <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap">
                            {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ", " + new Date(order.createdAt.seconds * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "-"}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Status</div>
                          <div className={`text-sm font-medium italic md:whitespace-nowrap ${
                            isPaid ? "text-red-600" : "text-amber-500"
                          }`}>
                            {isPaid ? "Paid" : "Pending Payment"}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Formulir Pemesanan - Only show if NO active/pending orders */}
            {!shuttleOrders.some(order => 
              order.transaction_status === "settlement" || 
              order.transaction_status === "capture" || 
              order.transaction_status === "pending"
            ) && (
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border border-slate-200 p-4"
            >
              {/* Integrated Search Selector */}
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Pesan Tiket Shuttle</h3>
                    <p className="text-sm text-gray-500">Pilih jadwal perjalanan Anda untuk ULTRA24</p>
                  </div>
                  <div className="relative self-start">
                    <InputField
                      type="select"
                      value={tripType}
                      onChange={(e) => {
                        setTripType(e.target.value);
                        setSelectedDepartureDate("");
                        setSelectedDepartureTime("");
                        setSelectedReturnTime("");
                        setSelectedOrderId(null);
                      }}
                      options={[
                        { label: "Pulang Pergi", value: "round-trip" },
                        { label: "Berangkat Saja", value: "one-way-departure" },
                        { label: "Pulang Saja", value: "one-way-return" },
                      ]}
                      defaultOptionLabel="Pilih Jenis"
                      endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                      className="w-auto"
                      labelClassName="sr-only"
                    />
                  </div>
                </div>
              </div>
              {/* Smart Selection UI */}
              <div className="space-y-4 mt-4">
                {/* Bagian Keberangkatan */}
                {tripType !== "one-way-return" && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-red-600 bg-white text-red-600 flex items-center justify-center text-sm font-bold">1</div>
                      <h4 className="text-lg font-bold text-gray-800 uppercase tracking-tight">Pilih Jadwal Keberangkatan</h4>
                    </div>

                    {/* Unified Info + Schedule Dropdown Container */}
                    <div className="border border-gray-200 rounded-2xl overflow-hidden">

                      {/* Route Info Row — balanced left/right */}
                      <div className="px-5 py-4 flex items-center gap-0 bg-gray-50">
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Keberangkatan</p>
                          <p className="text-sm font-bold text-gray-900">Jakarta</p>
                          <p className="text-xs text-gray-500 font-medium">{pickupPoints[0].meetingPoint}</p>
                        </div>

                        <div className="flex flex-col items-center gap-1 w-20 shrink-0">
                          <FontAwesomeIcon icon={faBus} size="sm" className="text-gray-400" />
                          <div className="w-14 h-[1px] bg-gray-300 relative text-gray-300">
                            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-1 bg-current rounded-full" />
                            <div className="absolute top-1/2 left-full -translate-y-1/2 -ml-1 w-1.5 h-1.5 border-t border-r border-current rotate-45" />
                          </div>
                          <p className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">3–4 Jam</p>
                        </div>

                        <div className="flex-1 text-right">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Tujuan</p>
                          <p className="text-sm font-bold text-gray-900">Cibodas</p>
                          <p className="text-xs text-gray-500 font-medium">Kawasan TNGGP</p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200" />

                      {/* Schedule Dropdown — 2 columns */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex gap-4">
                          {/* Left: Dropdown */}
                          <div className="flex-1">
                            <InputField
                              label="Jadwal Keberangkatan"
                              labelClassName="text-[11px] font-semibold text-gray-400 mb-2"
                              type="select"
                              value={selectedDepartureTime}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSelectedDepartureTime(val);
                                const schedule = departureSchedule.find(s => s.times.some(t => `${s.date} ${t} WIB` === val));
                                setSelectedDepartureDate(schedule ? schedule.date : "");
                                setSelectedOrderId(null);
                              }}
                              options={departureSchedule.flatMap(s => 
                                s.times.map(t => ({
                                  label: `${s.date} ${t} WIB`,
                                  value: `${s.date} ${t} WIB`
                                }))
                              )}
                              defaultOptionLabel="Pilih Jadwal"
                              endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            />
                          </div>

                          {/* Right: Info */}
                          <div className="w-36 shrink-0 border-l border-gray-100 pl-4">
                            <div className="h-4 mb-2" /> {/* Spacer for label alignment */}
                            <div className="h-[42px] flex items-center">
                              {selectedDepartureTime ? (() => {
                                const q = quotas.departure[pickupPoints[0].name]?.[selectedDepartureTime];
                                const booked = q?.booked ?? 0;
                                return (
                                  <p className="text-sm font-medium text-gray-800 tracking-tight">
                                    {booked} / {SHUTTLE_QUOTA} terisi
                                  </p>
                                );
                              })() : (
                                <p className="text-sm text-gray-300 font-normal">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                )}


                {/* Bagian Kepulangan */}
                {tripType !== "one-way-departure" && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-4 pt-4 border-t border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-red-600 bg-white text-red-600 flex items-center justify-center text-sm font-bold">2</div>
                      <h4 className="text-lg font-bold text-gray-800 uppercase tracking-tight">Pilih Jadwal Kepulangan</h4>
                    </div>

                    {/* Unified Info + Schedule Dropdown Container */}
                    <div className="border border-gray-200 rounded-2xl overflow-hidden">

                      {/* Route Info Row — balanced left/right */}
                      <div className="px-5 py-4 flex items-center gap-0 bg-gray-50">
                        <div className="flex-1">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Keberangkatan</p>
                          <p className="text-sm font-bold text-gray-900">Cibodas</p>
                          <p className="text-xs text-gray-500 font-medium">Kawasan TNGGP</p>
                        </div>

                        <div className="flex flex-col items-center gap-1 w-20 shrink-0">
                          <FontAwesomeIcon icon={faBus} size="sm" className="text-gray-400" />
                          <div className="w-14 h-[1px] bg-gray-300 relative text-gray-300">
                            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-1 bg-current rounded-full" />
                            <div className="absolute top-1/2 left-full -translate-y-1/2 -ml-1 w-1.5 h-1.5 border-t border-r border-current rotate-45" />
                          </div>
                          <p className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">3–4 Jam</p>
                        </div>

                        <div className="flex-1 text-right">
                          <p className="text-[11px] font-semibold text-gray-400 mb-0.5">Tujuan</p>
                          <p className="text-sm font-bold text-gray-900">Jakarta</p>
                          <p className="text-xs text-gray-500 font-medium">GBK / Parkir Timur</p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200" />

                      {/* Schedule Dropdown — 2 columns */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex gap-4">
                          {/* Left: Dropdown */}
                          <div className="flex-1">
                            <InputField
                              label="Jadwal Kepulangan"
                              labelClassName="text-[11px] font-semibold text-gray-400 mb-2"
                              type="select"
                              value={selectedReturnTime}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSelectedReturnTime(val);
                                const schedule = returnSchedule.find(s => s.times.some(t => `${s.date} ${t} WIB` === val));
                                setSelectedReturnDate(schedule ? schedule.date : "");
                                setSelectedOrderId(null);
                              }}
                              options={returnSchedule.flatMap(s => 
                                s.times.map(t => ({
                                  label: `${s.date} ${t} WIB`,
                                  value: `${s.date} ${t} WIB`
                                }))
                              )}
                              defaultOptionLabel="Pilih Jadwal"
                              endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            />
                          </div>

                          {/* Right: Info */}
                          <div className="w-36 shrink-0 border-l border-gray-100 pl-4">
                            <div className="h-4 mb-2" /> {/* Spacer for label alignment */}
                            <div className="h-[42px] flex items-center">
                              {selectedReturnTime ? (() => {
                                const q = quotas.return?.[selectedReturnTime];
                                const booked = q?.booked ?? 0;
                                return (
                                  <p className="text-sm font-medium text-gray-800 tracking-tight">
                                    {booked} / {SHUTTLE_QUOTA} terisi
                                  </p>
                                );
                              })() : (
                                <p className="text-sm text-gray-300 font-normal">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
            )}
          </div>

            {/* Sticky Sidebar / Information Area */}
            <div className="w-full lg:flex-1 lg:max-w-md space-y-4">
              <div className="sticky top-12 space-y-4">
                {(() => {
                  const activeOrder = shuttleOrders.find((o) => o.id === selectedOrderId);
                  const isPaidOrder = activeOrder ? (activeOrder.transaction_status === 'settlement' || activeOrder.transaction_status === 'capture') : false;
                
                  const displayTripType = activeOrder ? activeOrder.tripType : tripType;
                  const displayDeparture = activeOrder ? activeOrder.departureTime : selectedDepartureTime;
                  const displayReturn = activeOrder ? activeOrder.returnTime : selectedReturnTime;
                  const displayHarga = activeOrder ? activeOrder.harga : shuttleDetails.harga;

                  return (
                    <>
                      {/* Dynamic Order Summary Card */}
                      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-none">
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Ringkasan Pesanan</h4>
      
                        {/* Trip Type Row */}
                        <div className="flex items-start justify-between py-2.5 border-b border-gray-100">
                          <p className="text-[11px] font-semibold text-gray-400">Jenis Perjalanan</p>
                          <p className="text-sm font-medium text-gray-800 text-right">
                            {displayTripType === "round-trip" ? "Pulang Pergi" : displayTripType === "one-way-departure" ? "Berangkat Saja" : "Pulang Saja"}
                          </p>
                        </div>
      
                        {/* Departure Row */}
                        {displayTripType !== "one-way-return" && (
                          <div className="flex items-start justify-between py-2.5 border-b border-gray-100 gap-4">
                            <p className="text-[11px] font-semibold text-gray-400 shrink-0">Keberangkatan</p>
                            <p className="text-sm font-medium text-gray-800 text-right">
                              {displayDeparture ? displayDeparture : <span className="text-gray-300">—</span>}
                            </p>
                          </div>
                        )}
      
                        {/* Return Row */}
                        {displayTripType !== "one-way-departure" && (
                          <div className="flex items-start justify-between py-2.5 border-b border-gray-100 gap-4">
                            <p className="text-[11px] font-semibold text-gray-400 shrink-0">Kepulangan</p>
                            <p className="text-sm font-medium text-gray-800 text-right">
                              {displayReturn ? displayReturn : <span className="text-gray-300">—</span>}
                            </p>
                          </div>
                        )}
      
                        {/* Price Row */}
                        <div className="flex items-center justify-between pt-3 mt-1">
                          <p className="text-[11px] font-semibold text-gray-400">Total</p>
                          <p className="text-lg font-bold text-gray-900">Rp {Number(displayHarga).toLocaleString('id-ID')}<span className="text-xs font-normal text-gray-400 ml-1">/ pax</span></p>
                        </div>
                      </div>
      
                      {/* Ketentuan Card */}
                      <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 shadow-none">
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-4">Ketentuan</h4>
                        <ul className="space-y-3">
                          {[
                            "Hadir 30 menit sebelum jadwal",
                            "Siapkan E-Ticket & Kartu Identitas",
                            "Tiket Non-Refundable / No Reschedule",
                            "Maksimal 1 koper sedang + 1 tas kabin"
                          ].map((text, i) => (
                            <li key={i} className="flex items-start gap-3 text-[13px] text-gray-600">
                              <FontAwesomeIcon icon={faCheckCircle} size="xs" className="text-red-600 mt-0.5 shrink-0" />
                              {text}
                            </li>
                          ))}
                        </ul>
                      </div>
      
                      {/* CTA Button */}
                      <Button
                        onClick={activeOrder ? () => {
                           if(!isPaidOrder) handleBayar(activeOrder.snapToken);
                        } : handleButtonClick}
                        disabled={loading || (activeOrder && isPaidOrder)}
                        className={`w-full text-base font-bold rounded-xl shadow-none ${activeOrder && isPaidOrder ? "bg-slate-300 text-slate-500 hover:bg-slate-300 pointer-events-none" : ""}`}
                      >
                        {loading ? "Memproses..." : activeOrder ? (isPaidOrder ? "Sudah Lunas" : "Lanjutkan Pembayaran") : "Pesan Sekarang"}
                      </Button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>



      </div>
    </section>
  );
};
